import { Button } from '@/components/ui/button';
import { bakeHtml } from '@/lib/export-html';
import { useApp } from '@/store/useApp';

/**
 * Opens the current design in a new browser tab via a Blob URL. Bakes user edits +
 * tweak overrides in and strips the host's runtime injection / CSP meta, so the
 * preview is the same HTML you'd get from "Export → Standalone HTML" — minus the
 * download. Useful for full-screen review without the device-frame constraint.
 */
export function PreviewButton() {
  const artifactHtml = useApp((s) => s.artifactHtml);
  const edits = useApp((s) => s.edits);
  const tweakValues = useApp((s) => s.tweakValues);
  const isStreaming = useApp((s) => s.isStreaming);

  const enabled = Boolean(artifactHtml) && !isStreaming;

  function handlePreview(): void {
    if (!enabled) return;
    const baked = bakeHtml({
      html: artifactHtml,
      edits: edits.map((e) => ({ selector: e.selector, newText: e.newText })),
      tweaks: tweakValues.map((t) => ({ variable: t.variable, value: t.value })),
    });
    const blob = new Blob([baked], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    // Use a synthetic anchor click rather than window.open():
    //   - window.open(..., 'noopener,noreferrer') returns null even on success, so
    //     there's no reliable way to detect a blocked popup — and the previous
    //     fallback (location.assign) would always fire and replace the *current* tab.
    //   - <a target="_blank" rel="noopener noreferrer"> click is universally honored
    //     as a user-initiated navigation, gives us the same security model, and
    //     never touches the current tab.
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Defer revocation: the new tab needs the URL valid until it finishes loading.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={handlePreview}
      disabled={!enabled}
      title={enabled ? 'Open design in a new tab' : 'No design to preview yet'}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="mr-1 h-3.5 w-3.5"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Z"
          clipRule="evenodd"
        />
        <path
          fillRule="evenodd"
          d="M6.194 12.753a.75.75 0 0 0 1.06.053L16.5 4.44v2.81a.75.75 0 0 0 1.5 0v-4.5a.75.75 0 0 0-.75-.75h-4.5a.75.75 0 0 0 0 1.5h2.553l-9.056 8.194a.75.75 0 0 0-.053 1.06Z"
          clipRule="evenodd"
        />
      </svg>
      Preview
    </Button>
  );
}
